export interface StickyValueAxisProps {
  /**
   * 값영역 앞쪽 오프셋(px) = 카테고리 폭 + 좌 margin(라벨 컬럼, 값영역 좌측 시작). plot 값영역 시작과 일치해야 정렬.
   */
  leadOffset: number;
  /** 값영역 뒤쪽 오프셋(px) = 우 margin(스크롤바 gutter, 값영역 우측 끝). plot 값영역 끝과 일치. */
  trailOffset: number;
  /** 값축 도메인 [min,max]. */
  domain: [number, number];
  /** tick 값들(양끝 포함) — plot 그리드와 동일 값을 공유해 정렬. */
  ticks: number[];
  /** strip 두께(높이, px). */
  extent: number;
  /** 값 → 표시 텍스트(미지정 시 숫자). 단위·포맷은 소비처가 주입(이 컴포넌트는 단위를 모른다). */
  format?: (value: number) => string;
}

/**
 * 스크롤 밖에 **고정 표기**되는 값축(가로 막대의 상단 strip) — recharts 축은 series 없는 빈 차트에서 스케일을
 * 못 잡아 tick 이 안 그려짐. 대신 값영역(`[leadOffset, container - trailOffset]`)에 tick 라벨을 HTML 로 직접
 * 배치한다. plot 이 동일한 `leadOffset`(=카테고리 폭+margin)·`trailOffset`(=margin)·`domain` 을 쓰면 tick
 * 위치가 plot 그리드와 **대수적으로 일치**(단일 변수 공유 → drift 불가).
 *
 * `${frac}%` 는 tick 의 축상 위치(값→0~100)를 CSS `left` 로 쓰는 **레이아웃 좌표**일 뿐 — 값 단위 아님(단위는
 * `format` 이 결정).
 */
export function StickyValueAxis({
  leadOffset,
  trailOffset,
  domain,
  ticks,
  extent,
  format,
}: StickyValueAxisProps) {
  const span = domain[1] - domain[0] || 1;
  const fmt = (t: number) => (format ? format(t) : String(t));
  const frac = (t: number) => ((t - domain[0]) / span) * 100;

  return (
    <div className="flex text-xs text-muted-foreground" style={{ height: extent }}>
      <div style={{ width: leadOffset, flexShrink: 0 }} />
      <div className="relative flex-1" style={{ marginRight: trailOffset }}>
        {ticks.map((t) => (
          <span
            key={t}
            className="absolute top-0.5 -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${frac(t)}%` }}
          >
            {fmt(t)}
          </span>
        ))}
      </div>
    </div>
  );
}
